import { InvitationLayout } from '@shared/components/auth/InvitationLayout';
import { Button } from '@shared/components/ui/button';
import { Spinner } from '@shared/components/ui/Spinner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationOnboarding } from '../../hooks/useNotificationOnboarding';
import type { NotificationPreference } from '../../models/Notification.types';
import {
  NotificationPreferenceFields,
  type NotificationPreferenceState,
} from '../interaction/NotificationPreferenceFields';

export function NotificationOnboarding() {
  const { t } = useTranslation('notificationsModule');
  const { preferences, isLoading, isError, refetch, completion } = useNotificationOnboarding();
  const [draft, setDraft] = useState<Record<number, NotificationPreferenceState>>({});
  const values = Object.fromEntries(
    preferences.map((preference) => [
      preference.id,
      draft[preference.id] ?? {
        email: preference.enabled_email,
        inApp: preference.enabled_in_app,
      },
    ]),
  );
  const hasSelection = Object.values(values).some((value) => value.email || value.inApp);

  function toggle(preference: NotificationPreference, channel: 'inApp' | 'email') {
    setDraft((current) => ({
      ...current,
      [preference.id]: {
        ...values[preference.id],
        [channel]: !values[preference.id][channel],
      },
    }));
  }

  function save() {
    completion.mutate(
      hasSelection
        ? preferences.map((preference) => ({
            id: preference.id,
            enabled_email: values[preference.id].email,
            enabled_in_app: values[preference.id].inApp,
          }))
        : [],
    );
  }

  return (
    <InvitationLayout
      welcomeTitle={t('acceptInvitation.welcome', { ns: 'usersModule' })}
      welcomeDescription={t('notifications.onboarding.welcomeDescription')}
      contentClassName="flex h-full min-h-0 max-w-2xl flex-col gap-6 p-6 md:p-10 lg:p-12"
    >
      <header className="shrink-0 space-y-2">
        <h1 className="py-2 text-2xl font-bold text-card-title md:text-[28px]">
          {t('notifications.onboarding.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('notifications.onboarding.description')}</p>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
        {isLoading && (
          <div role="status" className="flex items-center gap-2 py-8">
            <Spinner size="sm" />
            {t('notifications.onboarding.loading')}
          </div>
        )}
        {isError && (
          <div role="alert" className="space-y-3 rounded-lg border border-border p-4">
            <p>{t('notifications.onboarding.loadError')}</p>
            <Button variant="outline" disabled={completion.isPending} onClick={() => void refetch()}>
              {t('notifications.error.retry')}
            </Button>
          </div>
        )}
        {preferences.length > 0 && (
          <NotificationPreferenceFields
            preferences={preferences}
            values={values}
            disabled={completion.isPending}
            onToggle={toggle}
          />
        )}
        {!isLoading && !isError && preferences.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('notifications.preferences.noPreferences')}</p>
        )}
      </div>
      <footer className="shrink-0 space-y-4 border-t border-border pt-4">
        {completion.isError && (
          <p role="alert" className="text-sm text-destructive">
            {t('notifications.onboarding.saveError')}
          </p>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          {hasSelection && (
            <Button
              variant="outline"
              className="h-11"
              disabled={completion.isPending}
              onClick={() => completion.mutate([])}
            >
              {t('notifications.onboarding.skip')}
            </Button>
          )}
          <Button className="h-11" disabled={completion.isPending} onClick={save}>
            {completion.isPending && <Spinner size="sm" />}
            {t(
              completion.isPending
                ? 'notifications.onboarding.saving'
                : hasSelection
                  ? 'notifications.onboarding.save'
                  : 'notifications.onboarding.skip',
            )}
          </Button>
        </div>
      </footer>
    </InvitationLayout>
  );
}
